import { useEffect, useRef, useState } from "react";
import { useUserId } from "../Contexts/user/useUserId";
import { Logger } from "../tools/debugLogger";

type WebSocketOptions = {
  onMessage?: (event: MessageEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  reconnect?: boolean;
  reconnectInterval?: number;
};

type WebSocketReturn = {
  isConnected: boolean;
  websocket: WebSocket | null;
  subscribeWebSocket: (eventType: string, callback: Callback) => void;
  unsubscribeWebSocket: (eventType: string, callback: Callback) => void;
  disconnect: () => void;
};
type Callback = (data: any) => void;
interface Callbacks {
  [eventType: string]: Callback[];
}
export const useWebSocketKubik = (
  options: WebSocketOptions = {}
): WebSocketReturn => {
  const {
    onMessage,
    onOpen,
    onClose,
    onError,
    reconnect = true,
    reconnectInterval = 300000,
  } = options;
  const logger = new Logger(undefined, true);
  const userId = useUserId();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<NodeJS.Timeout | null>(null);
  const [callbacks, setCallbacks] = useState<Callbacks>({});
  const [messageLast, setMessageLast] = useState<any | null>(null);

  const connect = (url: string) => {
    if (websocketRef.current) {
      websocketRef.current.close();
    }
    const websocket = new WebSocket(url);
    websocketRef.current = websocket;

    websocket.onopen = () => {
      logger.log("WebSocket connected.");
      setIsConnected(true);
      if (onOpen) onOpen();
    };

    // Обработка входящих сообщений
    websocket.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      logger.log("WebSocket Message received:", data);
      setMessageLast(data);
    };
    websocket.onclose = () => {
      setIsConnected(false);
      if (onClose) onClose();
      logger.log("WebSocket disconnected.");
      if (reconnect) {
        reconnectRef.current = setTimeout(() => {
          logger.log("WebSocket reconnected.");
          connect(url);
        }, reconnectInterval);
      }
    };

    websocket.onerror = (error: Event) => {
      logger.error("WebSocket error:", error);
      if (onError) onError(error);
    };
  };
  useEffect(() => {
    if (messageLast) {
      logger.log("WebSocket Callback received messageLast:", messageLast);
      logger.log("WebSocket Callback received callbacks:", callbacks);
      if (messageLast.type && callbacks[messageLast.type]) {
        callbacks[messageLast.type].forEach((callback) =>
          callback(messageLast.data)
        );
        setMessageLast(null);
      }
    }
  }, [messageLast, callbacks]);

  const disconnect = () => {
    if (
      websocketRef.current &&
      websocketRef.current.readyState === WebSocket.OPEN
    ) {
      websocketRef.current.close();
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
      }
    }
  };
  const subscribe = (eventType: string, callback: Callback) => {
    const newCallbacks = { ...callbacks };
    if (!newCallbacks[eventType]) {
      newCallbacks[eventType] = [];
    }
    newCallbacks[eventType].push(callback);
    logger.log("WebSocket subscribe callbacks:", newCallbacks);
    setCallbacks(newCallbacks);
  };
  /**
   * Отмена подписки на события WebSocket.
   * @param eventType - Тип события.
   * @param callback - Функция обратного вызова для удаления.
   */
  const unsubscribe = (eventType: string, callback: Callback) => {
    const newCallbacks = { ...callbacks };
    logger.log("WebSocket unsubscribe callbacks:", newCallbacks);
    if (newCallbacks[eventType]) {
      newCallbacks[eventType] = newCallbacks[eventType].filter(
        (cb) => cb !== callback
      );
      setCallbacks(newCallbacks);
    }
  };

  useEffect(() => {
    logger.log("useEffect userId", userId);
    if (!userId) return;
    const host =
      window.location.hostname === "viw.im" ? "viw.im" : "localhost:4000"; // Текущий хост (например, localhost:8000)
    const protocol = host === "viw.im" ? "wss" : "ws";
    const url = `${protocol}://${host}/ws/executor/${userId}`;
    connect(url);

    return () => {
      disconnect();
    };
  }, [userId]);

  return {
    isConnected,
    websocket: websocketRef.current,
    disconnect,
    subscribeWebSocket: subscribe,
    unsubscribeWebSocket: unsubscribe,
  };
};

export default useWebSocketKubik;
